/**
 * CountBadgeView — the neutral count pill (SE-6589).
 *
 * Extracted verbatim from the inline pill that lived in `KanbanColumnView`, so
 * a Code Connect template has a real, importable component to name. That was
 * the repo-side half of SE-6589's Count Badge blocker: the Figma side is a
 * separate, design-owned publish (see the ticket's routing note).
 *
 * One neutral treatment in BOTH states — populated and empty columns render
 * the same pill. `surface-muted` is the design's `background/background200`;
 * the label is 12px regular, not semibold. Zero visual change from the inline
 * markup it replaces: same box classes, same text classes, same test identity.
 *
 * Deliberately NOT built on the Badge atom's tone pairings. Every pairing there
 * is either a deep raw-shade tone or pairs its fill with `content-secondary`;
 * this pill is `surface-muted` + `content-primary`, which is a semantic-tier
 * pairing no existing tone expresses. Composing the two atoms directly keeps
 * the rendering identical rather than inventing a tone to approximate it.
 * @module components/molecules/CountBadge/CountBadgeView
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

/** Props for CountBadgeView. */
export interface CountBadgeProps {
  /** The count to display. */
  readonly count: number;
  /** Test/a11y identity, forwarded to the pill container. */
  readonly testID?: string;
}

/**
 * Renders the neutral count pill.
 * @param root0 - Component props
 * @param root0.count - The count to display
 * @param root0.testID - Test identity forwarded to the pill container
 * @returns The count pill element.
 */
const CountBadgeView = ({ count, testID }: CountBadgeProps) => (
  <Box testID={testID} className="rounded-full bg-surface-muted px-2 py-micro">
    <Text variant="caption" className="text-content-primary">
      {count}
    </Text>
  </Box>
);

CountBadgeView.displayName = "CountBadgeView";

export default memo(CountBadgeView);
